import type { AssessmentAssignment } from 'state';
import { recordDragDiagnosticError } from 'services/dragDiagnostics';

export const V4_ASSESSMENT_NEWS_DRAG_TYPE =
  'application/x-pm-v4-assessment-news';

export type V4DraggedNews = {
  newsId: number;
  timing: 'current' | 'past';
};

export type LaterAssignmentLedgerEntry = {
  newsId: number;
  instrumentCode: string;
  afterAssignmentId: number;
};

export const writeV4NewsDragData = (
  transfer: DataTransfer,
  payload: V4DraggedNews,
) => {
  let written = false;
  try {
    transfer.effectAllowed = 'copy';
    transfer.setData(V4_ASSESSMENT_NEWS_DRAG_TYPE, JSON.stringify(payload));
    written = true;
  } catch (error) {
    recordDragDiagnosticError('transfer.write_failed', error, 'custom');
    // Some browser integrations reject custom formats; try the text contract.
  }
  // Keep the existing assessment surface's text/plain contract as a fallback.
  try {
    transfer.setData('text/plain', String(payload.newsId));
    written = true;
  } catch (error) {
    recordDragDiagnosticError('transfer.write_failed', error, 'text');
    // Cancel the drag at the source if neither format could be written.
  }
  return written;
};

export const readV4NewsDragData = (
  transfer: Pick<DataTransfer, 'getData'>,
): V4DraggedNews | undefined => {
  const read = (type: string) => {
    try {
      return transfer.getData(type);
    } catch (error) {
      recordDragDiagnosticError(
        'transfer.read_failed', error,
        type === V4_ASSESSMENT_NEWS_DRAG_TYPE ? 'custom' : 'text',
      );
      return '';
    }
  };
  const encoded = read(V4_ASSESSMENT_NEWS_DRAG_TYPE);
  if (encoded) {
    try {
      const parsed = JSON.parse(encoded) as Partial<V4DraggedNews>;
      const newsId = Number(parsed.newsId);
      if (
        Number.isInteger(newsId) &&
        newsId > 0 &&
        (parsed.timing === 'current' || parsed.timing === 'past')
      ) {
        return { newsId, timing: parsed.timing };
      }
    } catch (_error) {
      // Fall through to the assessment tab's text/plain payload.
    }
  }

  const newsId = Number(read('text/plain'));
  return Number.isInteger(newsId) && newsId > 0
    ? { newsId, timing: 'past' }
    : undefined;
};

export const getAssessmentStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch (error) {
    recordDragDiagnosticError('ledger.storage_failed', error, 'getter');
    // The getter itself can throw, before getItem/setItem can be called.
    return undefined;
  }
};

export const laterAssignmentLedgerKey = ({
  sessionId,
  playerId,
  scenario,
  currentPeriod,
}: {
  sessionId: number;
  playerId: number;
  scenario?: string;
  currentPeriod: number;
}) =>
  [
    'markets-v4-assessment-links',
    sessionId,
    playerId,
    encodeURIComponent(scenario || 'assessment'),
    currentPeriod,
  ].join(':');

export const readLaterAssignmentLedger = (
  storage: Pick<Storage, 'getItem'> | undefined,
  key: string | undefined,
): LaterAssignmentLedgerEntry[] => {
  if (!storage || !key) return [];
  try {
    const parsed = JSON.parse(storage.getItem(key) || '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is LaterAssignmentLedgerEntry =>
        Number.isInteger(entry?.newsId) &&
        entry.newsId > 0 &&
        typeof entry.instrumentCode === 'string' &&
        entry.instrumentCode.length > 0 &&
        Number.isInteger(entry.afterAssignmentId) &&
        entry.afterAssignmentId >= 0,
    );
  } catch (_error) {
    recordDragDiagnosticError('ledger.storage_failed', _error, 'read');
    return [];
  }
};

export const writeLaterAssignmentLedger = (
  storage: Pick<Storage, 'setItem'> | undefined,
  key: string | undefined,
  entries: LaterAssignmentLedgerEntry[],
) => {
  if (!storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify(entries));
  } catch (_error) {
    recordDragDiagnosticError('ledger.storage_failed', _error, 'write');
    // Storage can be unavailable in privacy modes; the live UI still works.
  }
};

export const matchingLaterAssignment = (
  entry: LaterAssignmentLedgerEntry,
  assignments: AssessmentAssignment[],
) => {
  const matches = assignments.filter(
    assignment =>
      assignment.news === entry.newsId &&
      assignment.instrument_code === entry.instrumentCode,
  );
  const pending = matches.find(assignment => assignment.pending);
  if (pending) return pending;
  return matches
    .filter(assignment => assignment.id > entry.afterAssignmentId)
    .sort((left, right) => right.id - left.id)[0];
};

export const latestAssignment = (assignments: AssessmentAssignment[]) =>
  assignments.find(assignment => assignment.pending) ||
  [...assignments].sort((left, right) => right.id - left.id)[0];
